import { Box, type SxProps, Typography } from "@mui/material";
import { cardTitleStyle, overviewCardStyle } from "./backofficeTheme";
import { EditBase, TransformData, useRefresh } from "react-admin";
import { useFormState } from "react-hook-form";
import React from "react";
import { Form2 } from "./Form2";
import { SaveButton2 } from "../buttons/SaveButton2";

/**
 * A card and a form in one component. children are expected to be `XxxInput` elements
 * for updating the current record. A `SaveButton` will displayed at the bottom of the card,
 * whenever an input field becomes dirty.
 */
export function FormCard(
  props: React.PropsWithChildren<{
    resource?: string;
    id?: string;
    title: string;
    sx?: SxProps;
    cardRef?: React.Ref<any>;
    /** default: true */
    warnWhenUnsavedChanges?: boolean;
    transform?: TransformData;
    showSaveButton?: boolean;
  }>,
) {
  const {
    resource,
    id,
    title,
    sx,
    cardRef,
    warnWhenUnsavedChanges = true,
    transform,
    showSaveButton,
    children,
  } = props;
  const refresh = useRefresh();
  const onSuccess = () => {
    showSuccessToast("Änderungen erfolgreich gespeichert.");
    refresh();
  };

  return (
    <EditBase
      resource={resource}
      id={id}
      redirect={false}
      mutationMode="pessimistic"
      mutationOptions={{ onSuccess, onError: (error) => handleError("Fehler beim Speichern der Änderungen.", error) }}
      transform={transform}
    >
      <Form2 warnWhenUnsavedChanges={warnWhenUnsavedChanges}>
        <Box ref={cardRef} sx={sx ? ({ ...overviewCardStyle, ...sx } as any) : overviewCardStyle}>
          <Typography sx={cardTitleStyle}>{title}</Typography>
          {children}
          <AnimatedSaveButton show={showSaveButton} />
        </Box>
      </Form2>
    </EditBase>
  );
}

function AnimatedSaveButton(props: { show?: boolean }) {
  // useFormState().isDirty might differ from useFormState().dirtyFields
  // see https://github.com/react-hook-form/react-hook-form/issues/4740
  const { dirtyFields, isLoading } = useFormState();
  const isDirty = Object.keys(dirtyFields).length > 0;
  const show = isDirty || isLoading || props.show;
  const animationDuration = "0.5s";

  return (
    <div
      style={{
        marginTop: show ? 0 : "-20px",
        height: show ? "40px" : 0,
        transition: `margin-top ${animationDuration} ease-out, height ${animationDuration} ease-out`,
        overflow: "hidden",
      }}
    >
      <SaveButton2 sx={{ scale: show ? 1 : 0, transition: `scale ${animationDuration} ease-out` }} />
    </div>
  );
}
